import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ShareButtons from './ShareButtons';

/**
 * BoomBox Classic Article - JSU vs Southern (September 26, 2026)
 * Premium interactive article with three distinct visual sections:
 * A. Player Spotlights - 6-player carousel
 * B. Attendance/Stadium - 3 crowd photos around 38,973 section
 * C. Fan Weekend Gallery - 6-photo slideshow
 */
const BoomBoxClassicArticle = () => {
  // ============================================
  // SECTION A: PLAYER SPOTLIGHTS DATA
  // ============================================
  const [activePlayer, setActivePlayer] = useState(0);
  
  const players = [
    {
      name: 'Nate Blount IV',
      position: 'RB',
      school: 'Jackson State',
      award: 'Boombox Classic MVP - Offense',
      stats: [
        { label: 'CARRIES', value: '17' },
        { label: 'RUSH YDS', value: '135' },
        { label: 'TD', value: '2' },
        { label: 'AVG', value: '7.9' }
      ],
      impact: 97,
      summary: 'Blount dominated with a punishing ground attack, averaging nearly 8 yards per carry.',
      photo: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/2zyt52r1_IMG_1513.jpeg'
    },
    {
      name: 'Jared Lockhart',
      position: 'QB',
      school: 'Jackson State',
      award: 'Offensive Player of the Game',
      stats: [
        { label: 'COMP', value: '12' },
        { label: 'PASS YDS', value: '165' },
        { label: 'TD', value: '2' },
        { label: 'INT', value: '0' }
      ],
      impact: 95,
      summary: 'Lockhart orchestrated the offense with precision, connecting on key plays.',
      photo: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/j7jaoa1x_IMG_1516.jpeg'
    },
    {
      name: 'Tymon Wells',
      position: 'DL',
      school: 'Jackson State',
      award: 'Defensive Line Standout',
      stats: [
        { label: 'TACKLES', value: '0.5' },
        { label: 'SACKS', value: '1.5' },
        { label: 'QB HURRIES', value: '2' },
        { label: 'TFL', value: '1' }
      ],
      impact: 92,
      summary: 'Wells terrorized the Southern offensive line with his pass-rushing ability.',
      photo: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/560ifdwj_IMG_1518.jpeg'
    },
    {
      name: 'Keith Bass Jr.',
      position: 'LB',
      school: 'Jackson State',
      award: 'Boombox Classic MVP - Defense',
      stats: [
        { label: 'TACKLES', value: '7' },
        { label: 'SOLO', value: '5' },
        { label: 'QB HURRIES', value: '2' },
        { label: 'TFL', value: '1' }
      ],
      impact: 96,
      summary: 'Bass anchored the defense with relentless pressure on Southern\'s quarterback.',
      photo: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/k4he3pek_IMG_1515.jpeg'
    },
    {
      name: 'Nate Rembert',
      position: 'WR',
      school: 'Jackson State',
      award: 'Playmaker of the Game',
      stats: [
        { label: 'REC YDS', value: '87' },
        { label: 'TD', value: '1' },
        { label: 'LONG', value: '32' },
        { label: 'TARGETS', value: '8' }
      ],
      impact: 91,
      summary: 'Rembert made explosive plays down the field and found the end zone.',
      photo: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/t20gtvf7_IMG_1517.jpeg'
    },
    {
      name: 'Carmello Jones',
      position: 'LB',
      school: 'Jackson State',
      award: 'Defensive Playmaker',
      stats: [
        { label: 'TACKLES', value: '2' },
        { label: 'SACKS', value: '1' },
        { label: 'SACK YDS', value: '4' },
        { label: 'TFL', value: '1' }
      ],
      impact: 89,
      summary: 'Jones disrupted Southern\'s offensive line all game long with his tenacious play.',
      photo: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/pr9dwfii_IMG_1514.jpeg'
    }
  ];

  // ============================================
  // SECTION B: ATTENDANCE PHOTOS DATA
  // Use distinct images from the fan slideshow
  // ============================================
  const attendanceVideo = 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/qnat3wuh_IMG_1374.mov';

  // ============================================
  // SECTION C: FAN WEEKEND SLIDESHOW DATA
  // ============================================
  const [fanSlideIndex, setFanSlideIndex] = useState(0);
  const fanGalleryRef = useRef(null);
  
  const fanPhotos = [
    { src: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/3onhcfya_IMG_2838.webp', alt: 'SheLovesThee at BoomBox Classic', rotate: 90 },
    { src: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/00lwg24b_IMG_9905.jpeg', alt: 'Friends at BoomBox Classic', rotate: 0 },
    { src: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/1gdw9av1_IMG_9904.webp', alt: 'JSU Fans at BoomBox Classic', rotate: 90 },
    { src: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/db2fqlt6_IMG_6683_2026-09-28-21-33-17-156_aigc.webp', alt: 'Group photo at BoomBox Classic', rotate: 0 },
    { src: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/qxke6nms_snapgram.io_399650084315_%283%29.webp', alt: 'Fans at BoomBox Classic', rotate: 0 },
    { src: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/ktr4uv9b_IMG_1301.webp', alt: 'Final Score Scoreboard 35-17', rotate: 90 },
    { src: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/0kt9740w_IMG_1334.JPG', alt: 'JSU Players on Sideline', rotate: 0 },
    { src: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/t1s3vstl_IMG_1398.webp', alt: 'SheLovesThee with Press Credential', rotate: 90 },
    { src: 'https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/4ad2rcrk_IMG_1419.webp', alt: 'Group Photo at BoomBox', rotate: 0 }
  ];

  const goToFanSlide = (index) => {
    if (index < 0) index = fanPhotos.length - 1;
    if (index >= fanPhotos.length) index = 0;
    setFanSlideIndex(index);
  };

  return (
    <div className="boombox-article" data-testid="boombox-article">
      <style>{`
        .boombox-article {
          --boombox-navy: #00204E;
          --boombox-gold: #D4AF37;
          --boombox-light: #F8FAFC;
          --boombox-white: #FFFFFF;
          --boombox-border: #E2E8F0;
          --boombox-muted: #64748B;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: var(--boombox-light);
          min-height: 100vh;
        }

        /* Hero Section */
        .boombox-hero {
          position: relative;
          width: 100%;
          height: 450px;
          background: linear-gradient(135deg, var(--boombox-navy) 0%, #003366 100%);
          overflow: hidden;
        }

        @media (max-width: 768px) {
          .boombox-hero {
            height: 380px;
          }
        }

        .boombox-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, var(--boombox-navy) 0%, rgba(0,32,78,0.6) 50%, transparent 100%);
        }

        .boombox-hero-content {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 2.5rem;
          color: white;
          max-width: 900px;
        }

        @media (max-width: 640px) {
          .boombox-hero-content {
            padding: 1.5rem;
          }
        }

        .boombox-content {
          max-width: 1000px;
          margin: 0 auto;
          padding: 2rem 1.5rem;
        }

        .boombox-section {
          margin-bottom: 3rem;
        }

        .boombox-heading {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--boombox-navy);
          margin-bottom: 1rem;
          line-height: 1.2;
        }

        @media (max-width: 640px) {
          .boombox-heading {
            font-size: 1.375rem;
          }
        }

        .boombox-text {
          font-size: 1.0625rem;
          line-height: 1.75;
          color: #475569;
          margin-bottom: 1.25rem;
        }

        /* Player Spotlight Carousel */
        #playerSpotlightCarousel {
          background: var(--boombox-white);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          margin-bottom: 2rem;
        }

        .player-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          padding: 1rem;
          background: #F1F5F9;
          border-bottom: 1px solid var(--boombox-border);
        }

        .player-tab {
          flex: 1;
          min-width: 90px;
          padding: 0.625rem 0.75rem;
          border: none;
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          background: var(--boombox-white);
          color: var(--boombox-muted);
        }

        .player-tab:hover {
          background: #E2E8F0;
        }

        .player-tab.active {
          background: var(--boombox-navy);
          color: white;
        }

        .player-card {
          padding: 1.5rem;
        }

        .player-header {
          display: flex;
          gap: 1.25rem;
          align-items: flex-start;
          margin-bottom: 1.25rem;
        }

        .player-photo-wrapper {
          width: 100px;
          height: 120px;
          border-radius: 12px;
          overflow: hidden;
          flex-shrink: 0;
          background: #E2E8F0;
        }

        .player-photo-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: top center;
        }

        .player-info h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--boombox-navy);
          margin-bottom: 0.25rem;
        }

        .player-info .position {
          font-size: 0.875rem;
          color: var(--boombox-muted);
          margin-bottom: 0.5rem;
        }

        .player-info .award {
          display: inline-block;
          background: linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%);
          color: var(--boombox-navy);
          font-size: 0.75rem;
          font-weight: 700;
          padding: 0.375rem 0.75rem;
          border-radius: 6px;
        }

        .player-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0.75rem;
          margin-bottom: 1.25rem;
        }

        @media (max-width: 480px) {
          .player-stats {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .stat-box {
          background: #F8FAFC;
          border: 1px solid var(--boombox-border);
          border-radius: 10px;
          padding: 0.875rem;
          text-align: center;
        }

        .stat-box .label {
          font-size: 0.625rem;
          font-weight: 700;
          text-transform: uppercase;
          color: var(--boombox-muted);
          margin-bottom: 0.25rem;
        }

        .stat-box .value {
          font-size: 1.375rem;
          font-weight: 700;
          color: var(--boombox-navy);
        }

        .impact-bar-wrapper {
          margin-bottom: 1rem;
        }

        .impact-bar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .impact-bar-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--boombox-muted);
        }

        .impact-bar-value {
          font-size: 0.75rem;
          font-weight: 700;
          color: var(--boombox-navy);
          background: #FEF3C7;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
        }

        .impact-bar {
          height: 10px;
          background: #E2E8F0;
          border-radius: 5px;
          overflow: hidden;
        }

        .impact-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--boombox-navy) 0%, #0066CC 100%);
          border-radius: 5px;
          transition: width 0.5s ease;
        }

        .player-summary {
          font-size: 0.9375rem;
          color: #475569;
          line-height: 1.6;
          font-style: italic;
        }

        /* Attendance Photo Block */
        #attendancePhotoBlock {
          background: var(--boombox-white);
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }

        .attendance-header {
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .attendance-number {
          font-size: 3rem;
          font-weight: 800;
          color: var(--boombox-navy);
          line-height: 1;
          margin-bottom: 0.5rem;
        }

        @media (max-width: 640px) {
          .attendance-number {
            font-size: 2.25rem;
          }
        }

        .attendance-subtitle {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--boombox-navy);
          margin-bottom: 0.5rem;
        }

        .attendance-caption {
          font-size: 0.9375rem;
          color: var(--boombox-muted);
        }

        .attendance-photos {
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: 1rem;
        }

        @media (max-width: 768px) {
          .attendance-photos {
            grid-template-columns: 1fr;
          }
        }

        .attendance-main {
          border-radius: 12px;
          overflow: hidden;
          height: 280px;
        }

        @media (max-width: 768px) {
          .attendance-main {
            height: 220px;
          }
        }

        .attendance-main img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .attendance-side {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .attendance-side-item {
          border-radius: 12px;
          overflow: hidden;
          height: 132px;
        }

        @media (max-width: 768px) {
          .attendance-side-item {
            height: 180px;
          }
        }

        .attendance-side-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        /* Fan Weekend Carousel */
        #fanWeekendCarousel {
          background: var(--boombox-white);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }

        .fan-carousel-header {
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid var(--boombox-border);
        }

        .fan-carousel-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--boombox-navy);
          margin-bottom: 0.25rem;
        }

        .fan-carousel-subtitle {
          font-size: 0.875rem;
          color: var(--boombox-muted);
        }

        .fan-main-image {
          position: relative;
          width: 100%;
          height: 400px;
          background: #1E293B;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @media (max-width: 768px) {
          .fan-main-image {
            height: 280px;
          }
        }

        .fan-main-image img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }

        .fan-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: rgba(255,255,255,0.9);
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          color: var(--boombox-navy);
        }

        .fan-nav-btn:hover {
          background: white;
          transform: translateY(-50%) scale(1.05);
        }

        .fan-nav-btn.prev {
          left: 1rem;
        }

        .fan-nav-btn.next {
          right: 1rem;
        }

        .fan-counter {
          position: absolute;
          bottom: 1rem;
          right: 1rem;
          background: rgba(0,0,0,0.7);
          color: white;
          font-size: 0.8125rem;
          font-weight: 600;
          padding: 0.5rem 0.875rem;
          border-radius: 6px;
        }

        .fan-thumbnails {
          display: flex;
          gap: 0.5rem;
          padding: 1rem;
          overflow-x: auto;
          background: #F8FAFC;
        }

        .fan-thumbnail {
          width: 70px;
          height: 52px;
          border-radius: 6px;
          overflow: hidden;
          cursor: pointer;
          flex-shrink: 0;
          border: 2px solid transparent;
          transition: all 0.2s;
          opacity: 0.6;
        }

        .fan-thumbnail:hover {
          opacity: 0.9;
        }

        .fan-thumbnail.active {
          border-color: var(--boombox-navy);
          opacity: 1;
        }

        .fan-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        /* Score Box */
        .score-box {
          background: linear-gradient(135deg, var(--boombox-navy) 0%, #003366 100%);
          color: white;
          padding: 2rem;
          border-radius: 16px;
          text-align: center;
          margin-bottom: 2rem;
        }

        .score-matchup {
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          opacity: 0.8;
          margin-bottom: 0.5rem;
        }

        .score-final {
          font-size: 3.5rem;
          font-weight: 800;
          line-height: 1;
          margin-bottom: 0.5rem;
        }

        @media (max-width: 640px) {
          .score-final {
            font-size: 2.5rem;
          }
        }

        .score-teams {
          font-size: 1.125rem;
          font-weight: 600;
        }

        .score-detail {
          margin-top: 1rem;
          font-size: 0.875rem;
          opacity: 0.85;
        }

        /* Sources */
        .boombox-sources {
          background: var(--boombox-white);
          padding: 1.5rem;
          border-radius: 12px;
          margin-top: 2rem;
        }

        .boombox-sources-title {
          font-size: 1rem;
          font-weight: 700;
          color: var(--boombox-navy);
          margin-bottom: 0.75rem;
        }

        .boombox-sources-text {
          font-size: 0.875rem;
          color: var(--boombox-muted);
          line-height: 1.6;
        }
      `}</style>

      {/* Hero Section */}
      <div className="boombox-hero" data-testid="boombox-hero">
        <div className="boombox-hero-overlay" />
        <div className="boombox-hero-content">
          <p style={{ fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.15em', opacity: 0.85, marginBottom: '0.75rem' }}>
            SHELOVESTHEE • BOOMBOX CLASSIC 2026
          </p>
          <h1 style={{ fontSize: 'clamp(1.75rem, 5vw, 2.75rem)', fontWeight: 700, marginBottom: '0.75rem', lineHeight: 1.1 }}>
            JACKSON STATE DOMINATES SOUTHERN IN BOOMBOX CLASSIC
          </h1>
          <p style={{ fontSize: 'clamp(0.9375rem, 2.5vw, 1.0625rem)', opacity: 0.9, lineHeight: 1.55, maxWidth: '700px' }}>
            The Tigers rolled to a 35-17 victory over Southern in front of 38,973 fans at Veterans Memorial Stadium, clinching the rivalry showdown with a dominant second-half performance.
          </p>
        </div>
      </div>

      <div className="boombox-content">
        {/* Final Score Box */}
        <div className="score-box" data-testid="boombox-score">
          <div className="score-matchup">BoomBox Classic • September 26, 2026</div>
          <div className="score-final">35 – 17</div>
          <div className="score-teams">JACKSON STATE over SOUTHERN</div>
          <div className="score-detail">Veterans Memorial Stadium • Jackson, Miss.</div>
        </div>

        {/* Intro Text */}
        <section className="boombox-section">
          <p className="boombox-text">
            Jackson State entered the 2026 BoomBox Classic looking to make a statement against its longtime rival. The Tigers did just that, controlling the line of scrimmage and pulling away in the second half for a decisive 35-17 victory.
          </p>
          <p className="boombox-text">
            Behind a balanced offensive attack and a suffocating defense, Jackson State improved its record and sent a message to the rest of the SWAC: the Tigers are a legitimate contender.
          </p>
        </section>

        {/* ============================================ */}
        {/* SECTION A: PLAYER SPOTLIGHTS CAROUSEL */}
        {/* ============================================ */}
        <section className="boombox-section">
          <h2 className="boombox-heading">Player Spotlights</h2>
          <p className="boombox-text" style={{ marginBottom: '1.5rem' }}>
            These six players made the biggest impact in Jackson State's BoomBox Classic victory. Tap a name to see their stats.
          </p>

          <div id="playerSpotlightCarousel" data-testid="player-spotlight-carousel">
            {/* Player Tabs */}
            <div className="player-tabs">
              {players.map((player, idx) => (
                <button
                  key={idx}
                  className={`player-tab ${activePlayer === idx ? 'active' : ''}`}
                  onClick={() => setActivePlayer(idx)}
                  data-testid={`player-tab-${idx}`}
                >
                  {player.name.split(' ').pop()}
                </button>
              ))}
            </div>

            {/* Active Player Card */}
            <div className="player-card" data-testid="active-player-card">
              <div className="player-header">
                <div className="player-photo-wrapper">
                  <img 
                    src={players[activePlayer].photo} 
                    alt={players[activePlayer].name}
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div className="player-info">
                  <h3>{players[activePlayer].name}</h3>
                  <p className="position">{players[activePlayer].position} • {players[activePlayer].school}</p>
                  <span className="award">{players[activePlayer].award}</span>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="player-stats">
                {players[activePlayer].stats.map((stat, idx) => (
                  <div key={idx} className="stat-box">
                    <div className="label">{stat.label}</div>
                    <div className="value">{stat.value}</div>
                  </div>
                ))}
              </div>

              {/* Impact Bar */}
              <div className="impact-bar-wrapper">
                <div className="impact-bar-header">
                  <span className="impact-bar-label">Game Impact</span>
                  <span className="impact-bar-value">{players[activePlayer].impact}/100</span>
                </div>
                <div className="impact-bar">
                  <div 
                    className="impact-bar-fill" 
                    style={{ width: `${players[activePlayer].impact}%` }}
                  />
                </div>
              </div>

              <p className="player-summary">{players[activePlayer].summary}</p>
            </div>
          </div>
        </section>

        {/* Game Story */}
        <section className="boombox-section">
          <h2 className="boombox-heading">Game Recap</h2>
          <p className="boombox-text">
            Jackson State wasted no time establishing dominance. The Tigers' ground game, led by Nate Blount IV, consistently moved the chains and kept Southern's defense on its heels throughout the contest.
          </p>
          <p className="boombox-text">
            Quarterback Jared Lockhart delivered when called upon, connecting on key third-down conversions and finding the end zone twice through the air. Meanwhile, the Jackson State defense made life difficult for Southern all night.
          </p>
        </section>

        {/* ============================================ */}
        {/* SECTION B: ATTENDANCE / STADIUM VIDEO */}
        {/* ============================================ */}
        <div id="attendancePhotoBlock" data-testid="attendance-photo-block">
          <div className="attendance-header">
            <div className="attendance-number">38,973</div>
            <div className="attendance-subtitle">IN THEE VET</div>
            <p className="attendance-caption">A packed Veterans Memorial Stadium set the scene for the 2026 BoomBox Classic.</p>
          </div>

          <div style={{ borderRadius: '12px', overflow: 'hidden', marginTop: '1.5rem' }}>
            <video 
              controls 
              autoPlay 
              muted 
              loop
              playsInline
              style={{ width: '100%', height: 'auto', display: 'block' }}
            >
              <source src={attendanceVideo} type="video/quicktime" />
              <source src={attendanceVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        {/* More Game Story */}
        <section className="boombox-section">
          <p className="boombox-text">
            The atmosphere inside Veterans Memorial Stadium was electric from start to finish. The home crowd helped fuel the Tigers' momentum, especially in the decisive second half when Jackson State pulled away.
          </p>
          <p className="boombox-text">
            Southern showed fight early, but Jackson State's depth and talent proved to be the difference. By the fourth quarter, the outcome was no longer in doubt as the Tigers celebrated another BoomBox Classic victory.
          </p>
        </section>

        {/* ============================================ */}
        {/* SECTION C: FAN WEEKEND SLIDESHOW */}
        {/* ============================================ */}
        <section className="boombox-section">
          <h2 className="boombox-heading">SheLovesThee On the Scene</h2>
          <p className="boombox-text" style={{ marginBottom: '1.5rem' }}>
            Explore SheLovesThee's own photos from the weekend. Use the arrows or thumbnails to move through the gallery.
          </p>

          <div id="fanWeekendCarousel" data-testid="fan-weekend-carousel">
            <div className="fan-carousel-header">
              <div className="fan-carousel-title">BoomBox Classic Weekend Gallery</div>
              <div className="fan-carousel-subtitle">Photos by SheLovesThee</div>
            </div>

            {/* Main Image */}
            <div className="fan-main-image">
              <img 
                src={fanPhotos[fanSlideIndex].src}
                alt={fanPhotos[fanSlideIndex].alt}
                loading="lazy"
                style={fanPhotos[fanSlideIndex].rotate ? { 
                  transform: `rotate(${fanPhotos[fanSlideIndex].rotate}deg) scale(0.6)`,
                  maxHeight: '100%',
                  maxWidth: '100%'
                } : {}}
              />
              
              {/* Navigation Arrows */}
              <button 
                className="fan-nav-btn prev"
                onClick={() => goToFanSlide(fanSlideIndex - 1)}
                aria-label="Previous photo"
                data-testid="fan-carousel-prev"
              >
                <ChevronLeft size={24} />
              </button>
              <button 
                className="fan-nav-btn next"
                onClick={() => goToFanSlide(fanSlideIndex + 1)}
                aria-label="Next photo"
                data-testid="fan-carousel-next"
              >
                <ChevronRight size={24} />
              </button>

              {/* Counter */}
              <div className="fan-counter">
                {fanSlideIndex + 1} of {fanPhotos.length}
              </div>
            </div>

            {/* Thumbnails */}
            <div className="fan-thumbnails">
              {fanPhotos.map((photo, idx) => (
                <div 
                  key={idx}
                  className={`fan-thumbnail ${fanSlideIndex === idx ? 'active' : ''}`}
                  onClick={() => goToFanSlide(idx)}
                  data-testid={`fan-thumbnail-${idx}`}
                >
                  <img 
                    src={photo.src} 
                    alt={photo.alt} 
                    loading="lazy"
                    style={photo.rotate ? { transform: `rotate(${photo.rotate}deg)` } : {}}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Closing */}
        <section className="boombox-section">
          <p className="boombox-text">
            With the BoomBox Classic victory in hand, Jackson State continues to build momentum heading into the heart of SWAC play. The Tigers proved they have the talent, depth, and home-field advantage to compete for a conference championship.
          </p>
        </section>

        {/* Sources */}
        <div className="boombox-sources" data-testid="boombox-sources">
          <h3 className="boombox-sources-title">Photo Credits</h3>
          <p className="boombox-sources-text">
            Game photos and stadium images courtesy of Jackson State Athletics and SheLovesThee Podcast. Player statistics from official game records.
          </p>
        </div>

        {/* Back to News / Related Articles */}
        <div style={{ 
          marginTop: '3rem', 
          padding: '2rem', 
          background: 'linear-gradient(135deg, #001f3f 0%, #003366 100%)', 
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <p style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Continue Reading
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <a 
              href="/news" 
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                background: '#007AFF', 
                color: 'white', 
                padding: '0.75rem 1.5rem', 
                borderRadius: '9999px', 
                fontWeight: 'bold', 
                textTransform: 'uppercase',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'transform 0.2s'
              }}
              data-testid="back-to-news-btn"
            >
              📰 Back to News
            </a>
            <a 
              href="/jackson-state-soccer" 
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                background: 'transparent', 
                color: 'white', 
                padding: '0.75rem 1.5rem', 
                borderRadius: '9999px', 
                fontWeight: 'bold', 
                textTransform: 'uppercase',
                textDecoration: 'none',
                fontSize: '0.875rem',
                border: '2px solid white',
                transition: 'transform 0.2s'
              }}
              data-testid="related-article-btn"
            >
              ⚽ JSU Soccer Preview
            </a>
          </div>
          
          {/* Share Buttons */}
          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', fontWeight: 'bold', marginBottom: '0.75rem', textTransform: 'uppercase' }}>Share This Article</p>
            <ShareButtons 
              title="Jackson State Dominates Southern in 2026 BoomBox Classic"
              url={typeof window !== 'undefined' ? window.location.href : ''}
              description="The Tigers rolled to a 35-17 victory in front of 38,973 fans at Veterans Memorial Stadium."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoomBoxClassicArticle;
